// Calculator component - Updated to use unified pricing service for additional costs
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Typography,
  Card,
  Space,
  Select,
  InputNumber,
  Button,
  Row,
  Col,
  Divider,
  Alert,
  Checkbox,
  DatePicker,
  Spin,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { useTariffs } from "../hooks/useTariffs";
import { useTransportTypes } from "../hooks/useTransportTypes";
import { useIncoterms } from "../hooks/useIncoterms";
import { useCostOverviewDisplayOrder } from "../hooks/useCostOverviewDisplayOrder";
import { formatEuro } from "../components/BookingWizard/PricingCalculatorHelpers";
import { trackFeatureUsage } from "../lib/posthog";
import { supabase } from "../lib/supabase";
import dayjs from "dayjs";
import {
  calculatePricingFromTemplates,
  type PricingContext,
  type BookingData,
  type PricingWarning,
} from "../services/unifiedPricingService";
import {
  getLmdCountries,
  getCartonsPerPallet,
  getPalletThreshold,
  CBM_PER_PALLET,
} from "../services/lmdRatesService";
import {
  getSelectedTariffForRoute,
  hasActiveOrFutureTariff,
  isTariffValidOnDate,
} from "../utils/calculatorTariffUtils";
import { usePortToAddressDistance } from "../hooks/usePortToAddressDistance";
import { getContainervervoerPrice } from "../utils/containervervoerPricing";
import { DeliveryAddressAutocomplete } from "../components/Calculator/DeliveryAddressAutocomplete";
import { ContainervervoerCalculationInfo } from "../components/Calculator/ContainervervoerCalculationInfo";
import { WarehouseToAddressMap } from "../components/Calculator/WarehouseToAddressMap";
import { useWarehouseToAddressRoute } from "../hooks/useWarehouseToAddressRoute";
import { useLatestRequestId } from "../hooks/useLatestRequestId";

const PRICING_CALC_DEBOUNCE_MS = 250;
import { PRELOADED_MAP_PATHS } from "../config/portToWarehouseRoutes";

const { Title, Text } = Typography;
const { Option } = Select;

// Styled calculator components
const CalculatorContainer = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
`;

const DisplayLabel = styled(Text)`
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  display: block;
  margin-bottom: 0;
`;

const DisplayValue = styled.div`
  font-size: 26px;
  font-weight: bold;
  font-variant-numeric: tabular-nums;
  color: rgba(0, 0, 0, 0.85);
`;

const InputGroup = styled.div`
  margin-bottom: 6px;
`;

const Label = styled(Text)`
  display: block;
  margin-bottom: 2px;
  font-weight: 500;
  font-size: 12px;
`;

const COUNTRY_CODE_MAP: Record<string, string> = {
  NETHERLANDS: "NL",
  "THE NETHERLANDS": "NL",
  HOLLAND: "NL",
  BELGIUM: "BE",
  "KINGDOM OF BELGIUM": "BE",
  GERMANY: "DE",
  FRANCE: "FR",
  SPAIN: "ES",
  ITALY: "IT",
  LUXEMBOURG: "LU",
  DENMARK: "DK",
  SWEDEN: "SE",
  NORWAY: "NO",
  FINLAND: "FI",
  POLAND: "PL",
  "UNITED KINGDOM": "GB",
  "GREAT BRITAIN": "GB",
  ENGLAND: "GB",
  UK: "GB",
};

const normalizeCountryCode = (value?: string | null): string | null => {
  if (!value || typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parenthesesMatch = trimmed.match(/\(([A-Z]{2})\)$/i);
  if (parenthesesMatch) {
    return parenthesesMatch[1].toUpperCase();
  }

  if (/^[A-Z]{2}$/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  const upper = trimmed.toUpperCase();
  if (COUNTRY_CODE_MAP[upper]) {
    return COUNTRY_CODE_MAP[upper];
  }

  const trailingCodeMatch = upper.match(/([A-Z]{2})$/);
  if (trailingCodeMatch) {
    return trailingCodeMatch[1].toUpperCase();
  }

  return null;
};

const BookingTypeTile = styled(Button)<{ selected?: boolean }>`
  width: 100%;
  height: 36px;
  border: 2px solid ${(props) => (props.selected ? "#1890ff" : "#d9d9d9")};
  background: ${(props) => (props.selected ? "#e6f7ff" : "#fff")};
  color: ${(props) => (props.selected ? "#1890ff" : "rgba(0, 0, 0, 0.85)")};
  font-weight: ${(props) => (props.selected ? 600 : 400)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;

  &:hover {
    border-color: #1890ff;
    color: #1890ff;
  }
`;

export const Calculator: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { tariffs, loading: loadingTariffs } = useTariffs();
  const { transportTypes, loading: loadingTransportTypes } = useTransportTypes();
  const { incoterms, loading: loadingIncoterms } = useIncoterms();
  const sortCostOverview = useCostOverviewDisplayOrder();

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // Read URL parameters and initialize state
  const urlTransportTypeId = searchParams.get("transportTypeId");
  const urlBookingType = searchParams.get("bookingType") as
    | "LCL"
    | "FCL"
    | null;
  const urlIncotermId = searchParams.get("incotermId");
  const urlRoute = searchParams.get("route");
  const urlExpectedDepartureDate = searchParams.get("expectedDepartureDate");
  const urlWeight = searchParams.get("weight");
  const urlVolume = searchParams.get("volume");
  const urlCartons = searchParams.get("cartons");
  const urlHsCodes = searchParams.get("hsCodes");
  const urlDeliveryType = searchParams.get("deliveryType") as
    | "delivery"
    | "storage"
    | null;
  const urlLmdCountry = searchParams.get("lmdCountry");
  const urlContainerSize = searchParams.get("containerSize");
  const urlPickupFee = searchParams.get("pickupFee");
  const urlAdditionalCosts = searchParams.get("additionalCosts");
  const urlAdditionalCostValues = searchParams.get("additionalCostValues");

  // Calculator inputs
  const [transportTypeId, setTransportTypeId] = useState<number | null>(
    urlTransportTypeId ? parseInt(urlTransportTypeId, 10) : null,
  );
  const [bookingType, setBookingType] = useState<"LCL" | "FCL" | null>(
    urlBookingType || null,
  ); // LCL = 1, FCL = 2
  const [incotermId, setIncotermId] = useState<number | null>(
    urlIncotermId ? parseInt(urlIncotermId, 10) : null,
  );
  const [selectedRoute, setSelectedRoute] = useState<string | null>(
    urlRoute || null,
  );
  const [expectedDepartureDate, setExpectedDepartureDate] = useState(() => {
    if (urlExpectedDepartureDate) {
      const parsed = dayjs(urlExpectedDepartureDate, "YYYY-MM-DD", true);
      if (parsed.isValid()) {
        // Don't allow past dates; use today if URL has a past date
        if (!parsed.isBefore(dayjs(), "day")) return parsed;
      }
    }
    return dayjs().add(7, "day");
  });
  const [weight, setWeight] = useState<number>(
    urlWeight ? parseFloat(urlWeight) : 1,
  );
  const [volume, setVolume] = useState<number>(() => {
    if (!urlVolume) return 1;
    const n = parseFloat(urlVolume);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });
  const [cartons, setCartons] = useState<number>(() => {
    if (!urlCartons) return 1;
    const n = parseInt(urlCartons, 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  });
  const [hsCodes, setHsCodes] = useState<number>(
    urlHsCodes ? parseInt(urlHsCodes, 10) : 1,
  );
  const [totalValue, setTotalValue] = useState<number>(0);
  const [cartonsPerPallet, setCartonsPerPallet] = useState<number>(8);
  const [palletThreshold, setPalletThreshold] = useState<number>(8);
  const [lmdSettingsLoaded, setLmdSettingsLoaded] = useState(false);
  const { next: nextPricingRequest, isCurrent: isCurrentPricingRequest } =
    useLatestRequestId();
  const [containerSize, setContainerSize] = useState<number | null>(
    urlContainerSize ? parseInt(urlContainerSize, 10) : null,
  );
  const [pickupFee, setPickupFee] = useState<number>(
    urlPickupFee ? parseFloat(urlPickupFee) : 0,
  );
  const [deliveryType, setDeliveryType] = useState<"delivery" | "storage">(
    urlDeliveryType || "storage",
  );
  const [overridePallets, setOverridePallets] = useState<number | null>(null);
  const [forcePalletShipment, setForcePalletShipment] =
    useState<boolean>(false); // Unchecked (disabled) by default — "Gebruik palletprijzen..."
  /** Geruild = true → no fee; false → €15/pallet */
  const [europalletsExchanged, setEuropalletsExchanged] = useState(true);
  const [lmdCountry, setLmdCountry] = useState<string>(urlLmdCountry || "NL");
  const [availableLmdCountries, setAvailableLmdCountries] = useState<string[]>(
    [],
  );

  // Initialize additional costs from URL
  const [selectedAdditionalCosts, setSelectedAdditionalCosts] = useState<
    Set<number>
  >(() => {
    if (urlAdditionalCosts) {
      return new Set(
        urlAdditionalCosts
          .split(",")
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id)),
      );
    }
    return new Set();
  });

  const [additionalCostValues, setAdditionalCostValues] = useState<
    Record<number, number>
  >(() => {
    if (urlAdditionalCostValues) {
      try {
        return JSON.parse(urlAdditionalCostValues);
      } catch (e) {
        console.error("Error parsing additionalCostValues from URL:", e);
        return {};
      }
    }
    return {};
  });

  const [deliveryAddress, setDeliveryAddress] = useState<{
    lat: number;
    lon: number;
    formatted: string;
  } | null>(null);

  // Pricing templates-based state (matching simulation tab)
  const [pricingTemplatesResult, setPricingTemplatesResult] = useState<{
    totalCost: number;
    components: Array<{
      name: string;
      totalPrice: number;
      unitPrice?: number;
      quantity?: number;
      calculationType?: string;
      calculationDetails?: string;
      componentType?: string;
      componentId?: number;
    }>;
    matchedTemplates: Record<string, unknown>[];
    matchingCriteria: string[];
    warnings: PricingWarning[];
  } | null>(null);

  // Additional costs state
  type AdditionalCostItem = {
    id: number;
    name: string;
    description: string | null;
    cost_eur: number | null;
    calculation_type: "fixed" | "user_input" | "percentage";
    default_value: number | null;
    max_value: number | null;
    applies_to_countries: string[] | null;
    applies_to_incoterms: Array<number | string> | null;
  };

  const [containerSizes, setContainerSizes] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [containerSizesLoading, setContainerSizesLoading] = useState(true);
  const [availableAdditionalCosts, setAvailableAdditionalCosts] = useState<
    AdditionalCostItem[]
  >([]);

  const handleAdditionalCostValueChange = (
    costId: number,
    value: number | null,
  ) => {
    setAdditionalCostValues((prev) => {
      const next = { ...prev };
      if (value === null || Number.isNaN(value)) {
        delete next[costId];
      } else {
        next[costId] = value;
      }
      return next;
    });
  };
  const parseNumericValue = (value: unknown): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  // Track feature usage
  useEffect(() => {
    trackFeatureUsage("tools_calculator_view");
  }, []);

  // Serialize selected additional costs for reliable effect dependency (Set reference can be stale)
  const selectedAdditionalCostsKey = Array.from(selectedAdditionalCosts)
    .sort((a, b) => a - b)
    .join(",");
  const additionalCostValuesKey = JSON.stringify(additionalCostValues);

  // Sync URL with calculator state so the link is shareable/saveable (includes additional costs)
  useEffect(() => {
    const params = new URLSearchParams();
    if (transportTypeId != null)
      params.set("transportTypeId", transportTypeId.toString());
    if (bookingType) params.set("bookingType", bookingType);
    if (incotermId != null) params.set("incotermId", incotermId.toString());
    if (selectedRoute) params.set("route", selectedRoute);
    if (expectedDepartureDate)
      params.set(
        "expectedDepartureDate",
        expectedDepartureDate.format("YYYY-MM-DD"),
      );
    if (weight !== 1) params.set("weight", weight.toString());
    if (volume !== 1) params.set("volume", volume.toString());
    if (cartons !== 1) params.set("cartons", cartons.toString());
    if (hsCodes !== 1) params.set("hsCodes", hsCodes.toString());
    if (deliveryType) params.set("deliveryType", deliveryType);
    if (lmdCountry) params.set("lmdCountry", lmdCountry);
    if (containerSize != null)
      params.set("containerSize", containerSize.toString());
    if (pickupFee > 0) params.set("pickupFee", pickupFee.toString());
    if (selectedAdditionalCosts.size > 0) {
      params.set(
        "additionalCosts",
        Array.from(selectedAdditionalCosts).join(","),
      );
    }
    if (Object.keys(additionalCostValues).length > 0) {
      params.set("additionalCostValues", JSON.stringify(additionalCostValues));
    }
    setSearchParams(params, { replace: true });
  }, [
    transportTypeId,
    bookingType,
    incotermId,
    selectedRoute,
    expectedDepartureDate,
    weight,
    volume,
    cartons,
    hsCodes,
    deliveryType,
    lmdCountry,
    containerSize,
    pickupFee,
    selectedAdditionalCostsKey,
    additionalCostValuesKey,
    setSearchParams,
  ]);

  // Skip reset effects on initial mount so URL params are preserved on reload
  const hasInitializedFromUrl = useRef(false);

  // Reset booking type and route when transport type changes (user action, not initial load)
  useEffect(() => {
    if (!hasInitializedFromUrl.current) return;
    if (transportTypeId !== 2) {
      setBookingType(null);
    }
    setSelectedRoute(null);
  }, [transportTypeId]);

  // Reset route when booking type changes (user action, not initial load)
  useEffect(() => {
    if (!hasInitializedFromUrl.current) return;
    setSelectedRoute(null);
  }, [bookingType]);

  // After first mount, allow resets on subsequent user changes
  useEffect(() => {
    hasInitializedFromUrl.current = true;
  }, []);

  // Fetch available LMD countries on mount (fallback to NL on error so Bezorgland stays visible)
  useEffect(() => {
    const fetchLmdCountries = async () => {
      try {
        const countries = await getLmdCountries();
        setAvailableLmdCountries(countries?.length ? countries : ["NL", "BE"]);
      } catch (error) {
        console.error("Error fetching LMD countries:", error);
        setAvailableLmdCountries(["NL", "BE"]);
      }
    };
    fetchLmdCountries();
  }, []);

  // Fetch LMD settings on mount
  useEffect(() => {
    const fetchLmdSettings = async () => {
      try {
        const [cartonsPerPalletVal, thresholdVal] = await Promise.all([
          getCartonsPerPallet(),
          getPalletThreshold(),
        ]);
        setCartonsPerPallet(cartonsPerPalletVal);
        setPalletThreshold(thresholdVal);
      } catch (error) {
        console.error("Error fetching LMD settings:", error);
      } finally {
        setLmdSettingsLoaded(true);
      }
    };
    fetchLmdSettings();
  }, []);

  // Fetch container sizes for FCL
  useEffect(() => {
    const fetchContainerSizes = async () => {
      try {
        setContainerSizesLoading(true);
        const { data, error } = await supabase
          .from("container_size")
          .select("id, name")
          .order("name");
        if (error) throw error;
        setContainerSizes((data as Array<{ id: number; name: string }>) || []);
      } catch (e) {
        console.error("Error fetching container sizes:", e);
      } finally {
        setContainerSizesLoading(false);
      }
    };
    fetchContainerSizes();
  }, []);

  const isCalculatorDataLoading =
    loadingTariffs ||
    loadingTransportTypes ||
    loadingIncoterms ||
    containerSizesLoading;

  // Fetch available additional costs on mount
  useEffect(() => {
    const fetchAdditionalCosts = async () => {
      try {
        console.log("[Calculator] Fetching additional costs...");
        const { data, error } = await supabase
          .from("additional_costs")
          .select(
            "id, name, description, cost_eur, calculation_type, default_value, max_value, applies_to_countries, applies_to_incoterms",
          )
          .eq("is_active", true);

        if (error) throw error;
        const costs = data || [];
        setAvailableAdditionalCosts(costs);
        setAdditionalCostValues((prev) => {
          const next = { ...prev };
          costs.forEach((cost) => {
            if (cost.calculation_type !== "fixed") {
              if (next[cost.id] === undefined) {
                next[cost.id] =
                  parseNumericValue(cost.default_value) ??
                  parseNumericValue(cost.cost_eur) ??
                  0;
              }
            }
          });
          return next;
        });
        console.log(
          "[Calculator] Additional costs fetched",
          Array.isArray(data) ? data.length : 0,
        );
      } catch (error) {
        console.error("Error fetching additional costs:", error);
      }
    };
    fetchAdditionalCosts();
  }, []);

  // Additional costs are now handled by unifiedPricingService

  // Pallets: from pallet_threshold (LMD settings). >= threshold → pallet mode; count = ceil(volume / 1.6).
  // Volume 0 still bills at least 1 pallet in pallet mode (LMD / outbound need a tier).
  const calculatedPallets = useMemo(() => {
    if (cartons < palletThreshold && !forcePalletShipment) {
      return 0;
    }
    return Math.max(1, Math.ceil(volume / CBM_PER_PALLET) || 1);
  }, [cartons, volume, forcePalletShipment, palletThreshold]);

  // Use override if provided, otherwise use calculated value
  const actualPallets =
    overridePallets !== null ? overridePallets : calculatedPallets;

  // LMD is now calculated inside the unified pricing service

  // Generate route options: use same date + container logic as selected tariff so label price matches details
  const routeOptions = useMemo(() => {
    if (!transportTypeId) return [];

    let availableTariffs = tariffs.filter(
      (tariff) => tariff.transport_type.id === transportTypeId,
    );

    if (transportTypeId === 2 && bookingType) {
      const expectedBookingsTypeId = bookingType === "FCL" ? 2 : 1;
      availableTariffs = availableTariffs.filter(
        (tariff) => tariff.bookings_type?.id === expectedBookingsTypeId,
      );
    }

    const date = expectedDepartureDate.startOf("day");
    const findClosestForRoute = (routeTariffs: typeof availableTariffs) => {
      const validOnDate = routeTariffs.filter((t) =>
        isTariffValidOnDate(t, expectedDepartureDate),
      );
      if (validOnDate.length > 0) return validOnDate;
      const future = routeTariffs
        .filter((t) => t.from && dayjs(t.from).isAfter(date))
        .sort((a, b) => dayjs(a.from!).valueOf() - dayjs(b.from!).valueOf());
      if (future.length > 0) return future;
      const past = routeTariffs
        .filter((t) => t.to && dayjs(t.to).isBefore(date))
        .sort((a, b) => dayjs(b.to!).valueOf() - dayjs(a.to!).valueOf());
      if (past.length > 0) return past;
      return routeTariffs;
    };

    const routeGroups: Record<string, typeof availableTariffs> = {};
    availableTariffs.forEach((tariff) => {
      const routeKey = `${tariff.origin_port.id}-${tariff.destination_port.id}`;
      if (!routeGroups[routeKey]) routeGroups[routeKey] = [];
      routeGroups[routeKey].push(tariff);
    });

    const today = dayjs().startOf("day");
    return Object.keys(routeGroups)
      .map((routeKey) => {
        const tariffsInRoute = routeGroups[routeKey];
        // Only show routes with at least one active or future tariff (exclude fully expired routes)
        if (!hasActiveOrFutureTariff(tariffsInRoute, today)) return null;
        const validTariffs = findClosestForRoute(tariffsInRoute).sort(
          (a, b) => {
            if (!a.from) return 1;
            if (!b.from) return -1;
            return dayjs(b.from).valueOf() - dayjs(a.from).valueOf();
          },
        );
        if (validTariffs.length === 0) return null;

        const firstTariff = tariffsInRoute[0];
        const isFCL =
          transportTypeId === 2 && firstTariff.bookings_type?.id === 2;
        const currentTariff =
          isFCL && containerSize != null
            ? (validTariffs.find(
                (t) =>
                  t.container_size != null &&
                  Number(t.container_size.id) === Number(containerSize),
              ) ?? validTariffs[0])
            : validTariffs[0];

        let label = `${firstTariff.origin_port.name} → ${firstTariff.destination_port.name}`;

        if (transportTypeId === 1 && currentTariff.price) {
          label += ` (€${currentTariff.price.toFixed(2)}/kg)`;
        } else if (transportTypeId === 2 && currentTariff.price) {
          if (currentTariff.bookings_type?.id === 2) {
            const bySize = validTariffs.filter((t) => t.container_size != null);
            if (bySize.length > 1 && containerSize == null) {
              const prices = bySize.map((t) => t.price);
              const minP = Math.min(...prices);
              const maxP = Math.max(...prices);
              label +=
                minP < maxP
                  ? ` (€${minP.toFixed(2)}–${maxP.toFixed(2)}/container)`
                  : ` (€${minP.toFixed(2)}/container)`;
            } else {
              label += ` (€${currentTariff.price.toFixed(2)}/container)`;
            }
          } else {
            const lclPrices = validTariffs
              .map((t) => t.price)
              .filter((p): p is number => typeof p === "number");
            const minP = lclPrices.length
              ? Math.min(...lclPrices)
              : currentTariff.price;
            const maxP = lclPrices.length
              ? Math.max(...lclPrices)
              : currentTariff.price;
            if (minP < maxP) {
              label += ` (€${minP.toFixed(2)}–${maxP.toFixed(2)}/cbm)`;
            } else {
              label += ` (€${minP.toFixed(2)}/cbm)`;
            }
          }
        }

        return {
          value: routeKey,
          label,
          tariffId: currentTariff.id,
        };
      })
      .filter(Boolean); // Remove null entries
  }, [
    transportTypeId,
    bookingType,
    tariffs,
    containerSize,
    expectedDepartureDate,
  ]);

  // Selected tariff and fallback flag from shared logic (dashboard + full calculator parity)
  const { selectedTariff, isTariffFallback } = useMemo(
    () =>
      getSelectedTariffForRoute({
        tariffs,
        selectedRoute,
        transportTypeId,
        bookingType,
        expectedDepartureDate,
        volume,
        containerSize: containerSize != null ? Number(containerSize) : undefined,
      }),
    [
      tariffs,
      selectedRoute,
      transportTypeId,
      bookingType,
      volume,
      containerSize,
      expectedDepartureDate,
    ],
  );

  const originPortId = useMemo(() => {
    if (!selectedRoute) return null;
    const [originId] = selectedRoute.split("-");
    return originId ? parseInt(originId, 10) : null;
  }, [selectedRoute]);

  const destinationPortId = useMemo(() => {
    if (!selectedRoute) return null;
    const [, destId] = selectedRoute.split("-");
    return destId ? parseInt(destId, 10) : null;
  }, [selectedRoute]);

  const isSeafreightFcl =
    transportTypeId === 2 && bookingType === "FCL" && incotermId != null;
  const isSeafreightLcl =
    transportTypeId === 2 && bookingType === "LCL";
  const isAirfreight = transportTypeId === 1;

  const deliveryDestinationForDistance = useMemo(
    () =>
      deliveryType === "delivery" && deliveryAddress
        ? {
            type: "coordinates" as const,
            lat: deliveryAddress.lat,
            lon: deliveryAddress.lon,
            formatted: deliveryAddress.formatted,
          }
        : null,
    [deliveryType, deliveryAddress?.lat, deliveryAddress?.lon, deliveryAddress?.formatted],
  );

  const {
    distanceKm,
    unknownKm,
    routeGeometry,
    portName,
    destLabel,
    portCoords,
    destCoords,
  } = usePortToAddressDistance({
    destinationPortId,
    deliveryType,
    deliveryDestination: deliveryDestinationForDistance,
  });

  const containervervoerResult = useMemo(() => {
    if (!isSeafreightFcl) return null;
    if (unknownKm) return { unknownKm: true as const, sellingPrice: 0, basePrice: 0, calculationFormula: null, distanceKm: undefined };
    if (distanceKm == null) return null;
    const { basePrice, sellingPrice, calculationFormula } = getContainervervoerPrice(distanceKm);
    return { unknownKm: false as const, sellingPrice, basePrice, distanceKm, calculationFormula };
  }, [isSeafreightFcl, unknownKm, distanceKm]);

  const warehouseDeliveryRoute = useWarehouseToAddressRoute({
    deliveryAddress:
      (isSeafreightLcl || isAirfreight) && deliveryType === "delivery"
        ? deliveryAddress
        : null,
  });

  const displayTotal =
    (pricingTemplatesResult?.totalCost ?? 0) +
    (containervervoerResult?.sellingPrice ?? 0);

  const applicableCountryCodes = useMemo(() => {
    const codes = new Set<string>();
    const deliveryCode = normalizeCountryCode(lmdCountry);
    if (deliveryCode) {
      codes.add(deliveryCode);
    }

    const destinationCountry = selectedTariff?.destination_port?.country;
    const destinationCode = normalizeCountryCode(destinationCountry);
    if (destinationCode) {
      codes.add(destinationCode);
    }

    return Array.from(codes);
  }, [lmdCountry, selectedTariff]);

  const lmdCountriesForSelect = useMemo(
    () =>
      availableLmdCountries.length > 0 ? availableLmdCountries : ["NL", "BE"],
    [availableLmdCountries],
  );

  const filteredAdditionalCosts = useMemo(() => {
    if (availableAdditionalCosts.length === 0) {
      console.log("[Calculator] No available additional costs to filter");
      return [];
    }

    if (applicableCountryCodes.length === 0) {
      console.log(
        "[Calculator] No country codes available; returning all additional costs",
      );
      return availableAdditionalCosts;
    }

    const filtered = availableAdditionalCosts.filter((cost) => {
      // Show export clearance for EXW (ID 1) and FCA (ID 2), but NOT for FOB (ID 3)
      const costNameLower = cost.name.toLowerCase();
      const isExportClearance =
        costNameLower.includes("export clearance") ||
        costNameLower.includes("exportvergunning") ||
        costNameLower.includes("export clearance license") ||
        costNameLower.includes("exportvergunning licentie");

      if (isExportClearance) {
        // FOB incoterm ID is 3 - hide export clearance for FOB
        if (incotermId === 3) {
          return false;
        }
        // Show for EXW (ID 1) and FCA (ID 2), and other incoterms except FOB
        // If no incoterm is selected yet, show it (user can select later)
        return true;
      }

      const matchesCountry =
        !cost.applies_to_countries ||
        cost.applies_to_countries.length === 0 ||
        cost.applies_to_countries
          .map((country) => normalizeCountryCode(country))
          .filter(Boolean)
          .some((code) => applicableCountryCodes.includes(code as string));

      if (!matchesCountry) {
        return false;
      }

      if (cost.applies_to_incoterms && cost.applies_to_incoterms.length > 0) {
        if (!incotermId) {
          return false;
        }
        const normalizedIncoterms = cost.applies_to_incoterms.map((id) =>
          typeof id === "number" ? id : Number(id),
        );
        return normalizedIncoterms.includes(incotermId);
      }

      return true;
    });
    console.log(
      "[Calculator] Filtered additional costs",
      filtered.length,
      "matching codes",
      applicableCountryCodes,
      "incoterm",
      incotermId,
    );
    return filtered;
  }, [availableAdditionalCosts, applicableCountryCodes, incotermId]);

  const hasPickupCostInAdditional = filteredAdditionalCosts.some((cost) => {
    const nameLower = (cost.name || "").toLowerCase();
    return nameLower.includes("ophaal") || nameLower.includes("pickup");
  });

  // Keep selected costs in sync with visible country-specific options
  useEffect(() => {
    setSelectedAdditionalCosts((prevSelected) => {
      const allowedIds = new Set(
        filteredAdditionalCosts.map((cost) => cost.id),
      );
      let changed = false;
      const nextSelected = new Set<number>();
      prevSelected.forEach((id) => {
        if (allowedIds.has(id)) {
          nextSelected.add(id);
        } else {
          changed = true;
        }
      });
      if (changed) {
        console.log(
          "[Calculator] Dropped deselected additional costs due to filter change",
          [...prevSelected].filter((id) => !allowedIds.has(id)),
        );
      }
      return changed ? nextSelected : prevSelected;
    });
  }, [filteredAdditionalCosts]);

  // Debounced pricing — avoids stale LMD results when fields change in quick succession
  useEffect(() => {
    if (!lmdSettingsLoaded) return;

    const requestId = nextPricingRequest();
    const timer = window.setTimeout(() => {
      void (async () => {
        if (!isCurrentPricingRequest(requestId)) return;

        if (
          !transportTypeId ||
          !incotermId ||
          !originPortId ||
          !destinationPortId
        ) {
          if (isCurrentPricingRequest(requestId)) {
            setPricingTemplatesResult(null);
          }
          return;
        }

        if (transportTypeId === 2 && !bookingType) {
          if (isCurrentPricingRequest(requestId)) {
            setPricingTemplatesResult(null);
          }
          return;
        }

        try {
          const expectedBookingTypeId =
            bookingType === "FCL" ? 2 : bookingType === "LCL" ? 1 : null;

          const effectiveContainerSize =
            containerSize ?? selectedTariff?.container_size?.id ?? undefined;

          const pricingContext: PricingContext = {
            transportTypeId,
            bookingTypeId: expectedBookingTypeId || undefined,
            incotermId,
            originPortId,
            destinationPortId,
            containerSize: effectiveContainerSize,
            matchDate: expectedDepartureDate.format("YYYY-MM-DD"),
          };

          const bookingData: BookingData = {
            totalCartons: cartons,
            totalPallets: actualPallets,
            uniqueProducts: hsCodes,
            totalWeight: weight,
            totalVolume: volume,
            uniqueHsCodesCount: hsCodes,
            totalValue: totalValue > 0 ? totalValue : undefined,
            containerSize: effectiveContainerSize,
          };

          const selectedContainerSizeName =
            effectiveContainerSize != null
              ? containerSizes.find(
                  (s) => Number(s.id) === Number(effectiveContainerSize),
                )?.name
              : selectedTariff?.container_size?.name;
          const result = await calculatePricingFromTemplates(
            pricingContext,
            bookingData,
            {
              selectedTariff,
              containerSizeName: selectedContainerSizeName,
              isStorageDelivery: deliveryType === "storage",
              formatEuro,
              t: (key: string, defaultValue?: string) => t(key, defaultValue),
              filterZeroComponents: true,
              deliveryType,
              lmdCountryCode:
                deliveryType === "delivery" && lmdCountry
                  ? lmdCountry
                  : undefined,
              destinationCountryCode:
                selectedTariff?.destination_port?.country ?? undefined,
              palletThreshold,
              selectedAdditionalCostIds: Array.from(selectedAdditionalCosts),
              additionalCostOverrides: additionalCostValues,
              pickupFee: pickupFee > 0 ? pickupFee : undefined,
              europalletsExchanged:
                cartons >= palletThreshold || forcePalletShipment
                  ? europalletsExchanged
                  : true,
            },
          );

          if (!isCurrentPricingRequest(requestId)) return;

          if (result.error) {
            setPricingTemplatesResult(null);
            return;
          }

          setPricingTemplatesResult({
            totalCost: result.totalCost,
            components: result.components.map((comp) => ({
              name: comp.name,
              totalPrice: comp.totalPrice,
              unitPrice: comp.unitPrice ?? comp.totalPrice,
              quantity: comp.quantity ?? 1,
              calculationType: comp.calculationType,
              calculationDetails: comp.calculationDetails,
              componentType: comp.componentType,
              componentId: comp.componentId,
            })),
            matchedTemplates: result.matchedTemplates,
            matchingCriteria: result.matchingCriteria,
            warnings: result.warnings ?? [],
          });
        } catch (error) {
          if (isCurrentPricingRequest(requestId)) {
            console.error("Error calculating pricing:", error);
            setPricingTemplatesResult(null);
          }
        }
      })();
    }, PRICING_CALC_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [
    lmdSettingsLoaded,
    transportTypeId,
    bookingType,
    incotermId,
    originPortId,
    destinationPortId,
    selectedTariff,
    weight,
    volume,
    cartons,
    actualPallets,
    hsCodes,
    totalValue,
    containerSize,
    pickupFee,
    deliveryType,
    lmdCountry,
    palletThreshold,
    europalletsExchanged,
    forcePalletShipment,
    selectedAdditionalCostsKey,
    additionalCostValuesKey,
    expectedDepartureDate,
    nextPricingRequest,
    isCurrentPricingRequest,
  ]);

  // Calculate total cost - only use template-based pricing (no fallback)
  const totalCost = pricingTemplatesResult?.totalCost || 0;
  const hasValidCalculation = pricingTemplatesResult !== null && totalCost > 0;
  const hasError = pricingTemplatesResult?.error || null;

  // Auto-select first route when transport type changes
  useEffect(() => {
    const first = routeOptions.find(
      (r): r is NonNullable<typeof r> => r != null,
    );
    if (first && !selectedRoute) {
      setSelectedRoute(first.value);
    }
  }, [routeOptions, selectedRoute]);

  // Reset calculator
  const handleReset = () => {
    setTransportTypeId(null);
    setBookingType(null);
    setIncotermId(null);
    setSelectedRoute(null);
    setExpectedDepartureDate(dayjs().add(7, "day"));
    setWeight(1);
    setVolume(1);
    setCartons(1);
    setHsCodes(1);
    setTotalValue(0);
    setContainerSize(null);
    setPickupFee(0);
    setDeliveryType("delivery");
    setDeliveryAddress(null);
    setLmdCountry("NL");
    setOverridePallets(null);
    setForcePalletShipment(false);
    setPricingTemplatesResult(null);
    setSelectedAdditionalCosts(new Set());
    trackFeatureUsage("tools_calculator_reset");
  };

  return (
    <div style={{ padding: "8px 0 16px", width: "100%" }}>
      <Space direction="vertical" size={4} style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "4px 12px",
            marginBottom: 0,
          }}
        >
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/tools")}
            style={{ padding: 0, height: "auto", fontSize: 13 }}
          >
            {t("common.back")}
          </Button>
          <Title level={3} style={{ margin: 0, fontSize: 18 }}>
            {t("tools.calculator.title")}
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("tools.calculator.description")}
          </Text>
          <div style={{ marginLeft: "auto" }}>
            {(transportTypeId || selectedRoute || incotermId) && (
              <Button
                size="small"
                icon={<UndoOutlined />}
                onClick={handleReset}
                title={t("tools.calculator.reset", "Reset")}
              >
                {t("tools.calculator.reset", "Reset")}
              </Button>
            )}
          </div>
        </div>

        <CalculatorContainer>
          {isCalculatorDataLoading ? (
            <Card size="small" bodyStyle={{ padding: "48px 24px" }}>
              <div style={{ textAlign: "center" }}>
                <Spin size="large" tip={t("common.loading", "Loading...")} />
              </div>
            </Card>
          ) : (
            <>
          {/* Calculator Display */}
          <Card
            size="small"
            style={{ marginBottom: 8, textAlign: "center" }}
            bodyStyle={{ padding: "8px 12px" }}
          >
            <DisplayLabel>
              {t("tools.calculator.totalEstimatedCost")}
            </DisplayLabel>
            <DisplayValue>
              {hasValidCalculation ? formatEuro(displayTotal) : "€0.00"}
            </DisplayValue>
          </Card>

          {/* Error Message */}
          {hasError && (
            <Alert
              message={
                <Space size={4}>
                  <Text style={{ fontSize: 11 }}>{hasError}</Text>
                </Space>
              }
              type="warning"
              style={{ marginBottom: 8 }}
              showIcon
            />
          )}

          {isTariffFallback && (
            <Alert
              message={t("tools.calculator.tariffFallbackWarning")}
              type="info"
              style={{ marginBottom: 8 }}
              showIcon
            />
          )}

          <Row gutter={12}>
            {/* Left Column - Transport, Route, Incoterm, Weight/Vol/Cartons, Pallets */}
            <Col xs={24} lg={8}>
              <Card size="small" bodyStyle={{ padding: "8px 12px" }}>
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  {/* Step 1: Transport Type - Always visible */}
                  <InputGroup>
                    <Label>{t("tools.calculator.transportType")}</Label>
                    <Select
                      placeholder={t("tools.calculator.selectTransportType")}
                      value={transportTypeId}
                      onChange={setTransportTypeId}
                      style={{ width: "100%" }}
                    >
                      {transportTypes.map((type) => (
                        <Option key={type.id} value={type.id}>
                          {type.name}
                        </Option>
                      ))}
                    </Select>
                  </InputGroup>

                  {/* Step 2: Booking Type - Only for Seafreight */}
                  {transportTypeId === 2 && (
                    <InputGroup>
                      <Label>{t("tools.calculator.bookingType")}</Label>
                      <Row gutter={12}>
                        <Col span={12}>
                          <BookingTypeTile
                            selected={bookingType === "LCL"}
                            onClick={() => setBookingType("LCL")}
                          >
                            LCL
                          </BookingTypeTile>
                        </Col>
                        <Col span={12}>
                          <BookingTypeTile
                            selected={bookingType === "FCL"}
                            onClick={() => setBookingType("FCL")}
                          >
                            FCL
                          </BookingTypeTile>
                        </Col>
                      </Row>
                    </InputGroup>
                  )}

                  {/* Container size - only for FCL, right after booking type */}
                  {transportTypeId === 2 && bookingType === "FCL" && (
                    <InputGroup>
                      <Label>
                        {t(
                          "tools.calculator.containerSize",
                          "Container Grootte",
                        )}
                      </Label>
                      <Select
                        value={containerSize}
                        onChange={(val) => setContainerSize(val ?? null)}
                        placeholder={t(
                          "tools.calculator.selectContainerSize",
                          "Select container size",
                        )}
                        style={{ width: "100%" }}
                        allowClear
                      >
                        {containerSizes.map((size) => (
                          <Option key={size.id} value={size.id}>
                            {size.name}
                          </Option>
                        ))}
                      </Select>
                    </InputGroup>
                  )}

                  {/* Expected departure date - before Route so tariff is correct when route is selected */}
                  {transportTypeId &&
                    (transportTypeId !== 2 || bookingType) && (
                      <InputGroup>
                        <Label>
                          {t(
                            "tools.calculator.expectedDepartureDate",
                            "Verwachtte vertrekdatum",
                          )}
                        </Label>
                        <DatePicker
                          value={expectedDepartureDate}
                          onChange={(d) =>
                            setExpectedDepartureDate(d || dayjs().add(7, "day"))
                          }
                          disabledDate={(current) =>
                            current ? current.isBefore(dayjs(), "day") : false
                          }
                          format="YYYY-MM-DD"
                          style={{ width: "100%" }}
                        />
                        <Text
                          style={{
                            fontSize: 11,
                            lineHeight: 1.35,
                            display: "block",
                            marginTop: 6,
                            color: "#d4380d",
                            fontWeight: 500,
                          }}
                        >
                          {t("tools.calculator.expectedDepartureDateHelp")}
                        </Text>
                      </InputGroup>
                    )}

                  {/* Step 3: Route - only after expected date (and container size for FCL) */}
                  {transportTypeId &&
                    (transportTypeId !== 2 || bookingType) &&
                    (transportTypeId !== 2 ||
                      bookingType !== "FCL" ||
                      containerSize != null) && (
                      <InputGroup>
                        <Label>{t("tools.calculator.route")}</Label>
                        <Select
                          placeholder={t("tools.calculator.selectRoute")}
                          value={selectedRoute}
                          onChange={setSelectedRoute}
                          style={{ width: "100%" }}
                          loading={routeOptions.length === 0}
                        >
                          {routeOptions
                            .filter(
                              (r): r is NonNullable<typeof r> => r != null,
                            )
                            .map((route) => (
                              <Option key={route.value} value={route.value}>
                                {route.label}
                              </Option>
                            ))}
                        </Select>
                        {selectedTariff && (
                          <div style={{ marginTop: 4 }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {t(
                                "tools.calculator.validityPeriod",
                                "Validity period:",
                              )}{" "}
                              {selectedTariff.from && selectedTariff.to ? (
                                <>
                                  {dayjs(selectedTariff.from).format(
                                    "DD MMM YYYY",
                                  )}{" "}
                                  -{" "}
                                  {dayjs(selectedTariff.to).format(
                                    "DD MMM YYYY",
                                  )}
                                </>
                              ) : selectedTariff.from ? (
                                <>
                                  {t("tools.calculator.from", "From")}{" "}
                                  {dayjs(selectedTariff.from).format(
                                    "DD MMM YYYY",
                                  )}
                                </>
                              ) : selectedTariff.to ? (
                                <>
                                  {t("tools.calculator.until", "Until")}{" "}
                                  {dayjs(selectedTariff.to).format(
                                    "DD MMM YYYY",
                                  )}
                                </>
                              ) : (
                                t(
                                  "tools.calculator.alwaysValid",
                                  "Always valid",
                                )
                              )}
                            </Text>
                            {bookingType === "LCL" &&
                              selectedTariff.tariff_bracket && (
                                <Text
                                  type="secondary"
                                  style={{
                                    fontSize: 11,
                                    display: "block",
                                    marginTop: 1,
                                  }}
                                >
                                  {t("tools.calculator.bracket", "Bracket")}:{" "}
                                  {selectedTariff.tariff_bracket.from}–
                                  {selectedTariff.tariff_bracket.to}{" "}
                                  {t("tools.calculator.cbm", "CBM")} → €
                                  {selectedTariff.price.toFixed(2)}/CBM
                                </Text>
                              )}
                            {bookingType === "FCL" &&
                              transportTypeId === 2 &&
                              selectedTariff.container_size && (
                                <Text
                                  type="secondary"
                                  style={{
                                    fontSize: 11,
                                    display: "block",
                                    marginTop: 1,
                                  }}
                                >
                                  {t(
                                    "tools.calculator.containerLabel",
                                    "Container",
                                  )}
                                  : {selectedTariff.container_size.name} → €
                                  {selectedTariff.price.toFixed(2)}/
                                  {t("tools.calculator.container", "container")}
                                </Text>
                              )}
                            <div style={{ marginTop: 1 }}>
                              <Text
                                type="secondary"
                                style={{ fontSize: 11, lineHeight: "1.3" }}
                              >
                                {t(
                                  "tools.calculator.etdExplanation",
                                  "The prices shown are based on the validity period of the selected tariff above. This tariff only applies to shipments within that period. If your shipment's departure date (ETD) falls outside this period, a different tariff may apply.",
                                )}
                              </Text>
                            </div>
                          </div>
                        )}
                      </InputGroup>
                    )}

                  {/* Step 4: Incoterm - After Route is selected */}
                  {selectedRoute && (
                    <InputGroup>
                      <Label>{t("tools.calculator.incoterm")}</Label>
                      <Select
                        placeholder={t("tools.calculator.selectIncoterm")}
                        value={incotermId}
                        onChange={setIncotermId}
                        style={{ width: "100%" }}
                      >
                        {incoterms.map((incoterm) => (
                          <Option key={incoterm.id} value={incoterm.id}>
                            {incoterm.name}
                          </Option>
                        ))}
                      </Select>
                    </InputGroup>
                  )}

                  {/* Step 5: Shipment Details - After Incoterm is selected */}
                  {incotermId && (
                    <>
                      <Divider style={{ margin: "6px 0" }} />

                      <Row gutter={12}>
                        <Col span={8}>
                          <InputGroup>
                            <Label>{t("tools.calculator.weight")} (kg)</Label>
                            <InputNumber
                              value={weight}
                              onChange={(val) => setWeight(val || 0)}
                              min={0}
                              step={0.1}
                              controls={true}
                              style={{ width: "100%" }}
                              placeholder="0"
                            />
                          </InputGroup>
                        </Col>
                        <Col span={8}>
                          <InputGroup>
                            <Label>{t("tools.calculator.volume")} (CBM)</Label>
                            <InputNumber
                              value={volume}
                              onChange={(val) => {
                                const n = Number(val);
                                setVolume(
                                  Number.isFinite(n) && n > 0 ? n : 0.01,
                                );
                              }}
                              min={0.01}
                              step={0.01}
                              controls={true}
                              style={{ width: "100%" }}
                              placeholder="0.01"
                            />
                          </InputGroup>
                        </Col>
                        <Col span={8}>
                          <InputGroup>
                            <Label>{t("tools.calculator.cartons")}</Label>
                            <InputNumber
                              value={cartons}
                              onChange={(val) =>
                                setCartons(
                                  val != null && Number(val) >= 1
                                    ? Math.floor(Number(val))
                                    : 1,
                                )
                              }
                              min={1}
                              step={1}
                              controls={true}
                              style={{ width: "100%" }}
                              placeholder="1"
                            />
                          </InputGroup>
                        </Col>
                      </Row>

                      {/* Force pallet shipment toggle — only when not already in pallet mode */}
                      {cartons < palletThreshold && (
                        <Row gutter={12}>
                          <Col span={24}>
                            <div style={{ marginTop: 4, marginBottom: 4 }}>
                              <Checkbox
                                checked={forcePalletShipment}
                                onChange={(e) =>
                                  setForcePalletShipment(e.target.checked)
                                }
                              >
                                <Text style={{ fontSize: 11 }}>
                                  {t(
                                    "tools.calculator.forcePalletShipment",
                                    "Force pallet shipment pricing (bypass automatic carton/pallet detection)",
                                  )}
                                </Text>
                              </Checkbox>
                            </div>
                          </Col>
                        </Row>
                      )}

                      {(cartons >= palletThreshold || forcePalletShipment) && (
                        <Row gutter={12}>
                          <Col span={12}>
                            <InputGroup>
                              <Label>
                                {t("tools.calculator.pallets", "Pallets")}
                              </Label>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <InputNumber
                                  value={
                                    overridePallets !== null
                                      ? overridePallets
                                      : calculatedPallets
                                  }
                                  onChange={(val) =>
                                    setOverridePallets(val ?? 0)
                                  }
                                  min={0}
                                  step={1}
                                  controls={true}
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                  placeholder={`Auto: ${calculatedPallets}`}
                                  disabled={overridePallets === null}
                                />
                                {overridePallets === null ? (
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() =>
                                      setOverridePallets(calculatedPallets)
                                    }
                                    title={t(
                                      "tools.calculator.editPalletsOverride",
                                      "Edit pallets override",
                                    )}
                                  />
                                ) : null}
                              </div>
                              {overridePallets !== null && (
                                <div style={{ marginTop: 2 }}>
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 11 }}
                                  >
                                    <Button
                                      type="link"
                                      size="small"
                                      onClick={() => setOverridePallets(null)}
                                      style={{ padding: 0, fontSize: 11 }}
                                    >
                                      Reset to auto ({calculatedPallets})
                                    </Button>
                                  </Text>
                                </div>
                              )}
                            </InputGroup>
                          </Col>
                          <Col span={12}>
                            <InputGroup>
                              <Label>
                                {t(
                                  "tools.calculator.europalletsExchanged",
                                  "Europallets geruild",
                                )}
                              </Label>
                              <Checkbox
                                checked={europalletsExchanged}
                                onChange={(e) =>
                                  setEuropalletsExchanged(e.target.checked)
                                }
                              >
                                <Text style={{ fontSize: 12 }}>
                                  {europalletsExchanged
                                    ? t(
                                        "tools.calculator.europalletsExchangedYes",
                                        "Ja — geen palletkosten",
                                      )
                                    : t(
                                        "tools.calculator.europalletsExchangedNo",
                                        "Nee — €15 per pallet",
                                      )}
                                </Text>
                              </Checkbox>
                            </InputGroup>
                          </Col>
                        </Row>
                      )}

                      {/* Show calculated pallets info */}
                      {cartons > 0 && (
                        <div style={{ marginTop: 4, marginBottom: 4 }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {cartons < palletThreshold &&
                            !forcePalletShipment ? (
                              <>
                                {cartons}{" "}
                                {t("tools.calculator.cartons", "cartons")} -{" "}
                                {t(
                                  "tools.calculator.noPalletFees",
                                  "No pallet fees apply",
                                )}
                              </>
                            ) : (
                              t("tools.calculator.cartonsFitOnPallets", {
                                defaultValue:
                                  "{{cartons}} {{cartonsLabel}} fit on {{pallets}} {{palletsLabel}} ({{formula}})",
                                cartons,
                                cartonsLabel: t(
                                  "tools.calculator.cartons",
                                  "cartons",
                                ),
                                pallets: actualPallets,
                                palletsLabel:
                                  actualPallets === 1
                                    ? t("tools.calculator.pallet", "pallet")
                                    : t("tools.calculator.pallets", "pallets"),
                                formula: t(
                                  "tools.calculator.palletsFromCbm",
                                  "volume ÷ 1.6 CBM per pallet",
                                ),
                              })
                            )}
                          </Text>
                        </div>
                      )}
                    </>
                  )}
                </Space>
              </Card>
            </Col>

            {/* Middle Column - HS codes through Bezorgland + Additional costs */}
            <Col xs={24} lg={8}>
              {(incotermId || deliveryType === "delivery") && (
                <Card size="small" bodyStyle={{ padding: "8px 12px" }}>
                  <Space
                    direction="vertical"
                    size={4}
                    style={{ width: "100%" }}
                  >
                    {incotermId && (
                      <Row gutter={12}>
                        <Col span={12}>
                          <InputGroup>
                            <Label>{t("tools.calculator.hsCodes")}</Label>
                            <InputNumber
                              value={hsCodes}
                              onChange={(val) => setHsCodes(val || 0)}
                              min={1}
                              step={1}
                              controls={true}
                              style={{ width: "100%" }}
                              placeholder="1"
                            />
                          </InputGroup>
                        </Col>
                      </Row>
                    )}

                    <InputGroup>
                      <Label>{t("tools.calculator.deliveryType")}</Label>
                      <Select
                        value={deliveryType}
                        onChange={(val) => {
                          if (val === "delivery" || val === "storage") {
                            setDeliveryType(val);
                            if (val === "storage") setDeliveryAddress(null);
                          }
                        }}
                        style={{ width: "100%" }}
                      >
                        <Option value="delivery">
                          {t("tools.calculator.delivery")}
                        </Option>
                        <Option value="storage">
                          {t("tools.calculator.storage")}
                        </Option>
                      </Select>
                    </InputGroup>

                    {deliveryType === "delivery" && (
                      <>
                        {(isSeafreightFcl || isSeafreightLcl || isAirfreight) && (
                          <InputGroup>
                            <Label>
                              {t(
                                "tools.calculator.deliveryAddress",
                                "Delivery Address",
                              )}
                            </Label>
                            <DeliveryAddressAutocomplete
                              value={deliveryAddress?.formatted}
                              onChange={(p) =>
                                setDeliveryAddress(
                                  p
                                    ? {
                                        lat: p.lat,
                                        lon: p.lon,
                                        formatted: p.formatted,
                                      }
                                    : null,
                                )
                              }
                              placeholder={t(
                                "tools.calculator.enterDeliveryAddress",
                                "Enter address for distance calculation",
                              )}
                            />
                          </InputGroup>
                        )}
                        <InputGroup>
                          <Label>
                            {t(
                              "tools.calculator.lmdCountry",
                              "Delivery Country",
                            )}
                          </Label>
                          <Select
                            value={
                              lmdCountriesForSelect.includes(lmdCountry)
                                ? lmdCountry
                                : lmdCountriesForSelect[0]
                            }
                            onChange={(val) => {
                              if (
                                val &&
                                typeof val === "string" &&
                                lmdCountriesForSelect.includes(val)
                              ) {
                                setLmdCountry(val);
                              }
                            }}
                            style={{ width: "100%" }}
                          >
                            {lmdCountriesForSelect.map((country) => (
                              <Option key={country} value={country}>
                                {country}
                              </Option>
                            ))}
                          </Select>
                        </InputGroup>
                      </>
                    )}

                    {isSeafreightFcl && (containervervoerResult != null || (portCoords && destCoords)) && (
                      <ContainervervoerCalculationInfo
                        portName={portName}
                        destLabel={destLabel}
                        distanceKm={distanceKm}
                        unknownKm={containervervoerResult?.unknownKm ?? false}
                        sellingPrice={containervervoerResult?.sellingPrice ?? 0}
                        calculationFormula={containervervoerResult?.calculationFormula ?? null}
                        portCoords={portCoords}
                        destCoords={destCoords}
                        routeGeometry={routeGeometry}
                        routeReady={
                          deliveryType === "storage" ||
                          (deliveryType === "delivery" && deliveryAddress != null)
                        }
                        preloadedMapSrc={
                          deliveryType === "storage" && destinationPortId
                            ? PRELOADED_MAP_PATHS[destinationPortId] ?? null
                            : null
                        }
                      />
                    )}

                    {(isSeafreightLcl || isAirfreight) &&
                      deliveryType === "delivery" &&
                      deliveryAddress && (
                        <WarehouseToAddressMap
                          destLabel={warehouseDeliveryRoute.destLabel}
                          distanceKm={warehouseDeliveryRoute.distanceKm}
                          destCoords={warehouseDeliveryRoute.destCoords}
                          routeGeometry={warehouseDeliveryRoute.routeGeometry}
                          loading={warehouseDeliveryRoute.loading}
                          error={warehouseDeliveryRoute.error}
                        />
                      )}

                    <Divider style={{ margin: "6px 0" }}>
                      {t(
                        "tools.calculator.additionalCosts",
                        "Aanvullende Kosten",
                      )}
                    </Divider>

                    <div style={{ marginBottom: 6 }}>
                      <Text
                        type="secondary"
                        style={{ fontSize: 11, lineHeight: "1.35" }}
                      >
                        {t(
                          "tools.calculator.additionalCostsExplanation",
                          "Aanvullende kosten, indien van toepassing, dit is puur voor indicatie totale kosten. Ophaalkosten betekenen niks, maar zijn als indicatie en zijn jouw verwachte kosten.",
                        )}
                      </Text>
                    </div>

                    {filteredAdditionalCosts.length > 0 ? (
                      <div style={{ marginBottom: 6 }}>
                        <Text
                          strong
                          style={{
                            fontSize: 13,
                            marginBottom: 6,
                            display: "block",
                          }}
                        >
                          Selecteer aanvullende kosten:
                        </Text>
                        <Space
                          direction="vertical"
                          size="small"
                          style={{ width: "100%" }}
                        >
                          {filteredAdditionalCosts.map((cost) => {
                            const defaultValue =
                              parseNumericValue(cost.default_value) ??
                              parseNumericValue(cost.cost_eur) ??
                              0;
                            const overrideValue = additionalCostValues[cost.id];
                            const amountPreview =
                              overrideValue ?? defaultValue ?? 0;
                            return (
                              <div key={cost.id}>
                                <Checkbox
                                  checked={selectedAdditionalCosts.has(cost.id)}
                                  onChange={(e) => {
                                    const newSelected = new Set(
                                      selectedAdditionalCosts,
                                    );
                                    if (e.target.checked) {
                                      newSelected.add(cost.id);
                                    } else {
                                      newSelected.delete(cost.id);
                                    }
                                    setSelectedAdditionalCosts(newSelected);
                                  }}
                                >
                                  {cost.name}
                                  {cost.description && (
                                    <div
                                      style={{
                                        fontSize: 11,
                                        color: "#666",
                                        marginTop: 1,
                                      }}
                                    >
                                      {cost.description}
                                    </div>
                                  )}
                                </Checkbox>
                                {selectedAdditionalCosts.has(cost.id) &&
                                  cost.calculation_type !== "fixed" && (
                                    <div style={{ marginTop: 2 }}>
                                      <InputNumber
                                        size="small"
                                        min={0}
                                        step={
                                          cost.calculation_type === "percentage"
                                            ? 0.1
                                            : 1
                                        }
                                        value={overrideValue ?? undefined}
                                        placeholder={
                                          defaultValue !== null
                                            ? defaultValue.toString()
                                            : undefined
                                        }
                                        formatter={
                                          cost.calculation_type === "percentage"
                                            ? (value) => `${value}`
                                            : undefined
                                        }
                                        onChange={(val) =>
                                          handleAdditionalCostValueChange(
                                            cost.id,
                                            val ?? null,
                                          )
                                        }
                                        style={{ width: 160 }}
                                      />
                                      <Text
                                        type="secondary"
                                        style={{
                                          fontSize: 11,
                                          marginLeft: 8,
                                        }}
                                      >
                                        {formatEuro(amountPreview)}
                                      </Text>
                                    </div>
                                  )}
                              </div>
                              );
                            })}
                        </Space>
                        {hasPickupCostInAdditional && (
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 11,
                              display: "block",
                              marginTop: 8,
                              lineHeight: 1.35,
                            }}
                          >
                            {t("tools.calculator.pickupCostsOnRequest")}
                            <a href="mailto:info@efreightforwarding.com">info@efreightforwarding.com</a>.
                          </Text>
                        )}
                      </div>
                    ) : (
                      <Text
                        type="secondary"
                        style={{ marginBottom: 6, fontSize: 11 }}
                      >
                        {applicableCountryCodes.length === 0
                          ? t(
                              "tools.calculator.selectRouteOrCountryForCosts",
                              "Select a route or delivery country first to see relevant additional costs.",
                            )
                          : t(
                              "tools.calculator.noAdditionalCosts",
                              "No additional costs available for this country selection.",
                            )}
                      </Text>
                    )}
                  </Space>
                </Card>
              )}
            </Col>

            {/* Right Column - Breakdown Display */}
            <Col xs={24} lg={8}>
              {hasValidCalculation ? (
                <Card
                  size="small"
                  title={t("tools.calculator.breakdown")}
                  bodyStyle={{ padding: "8px 12px" }}
                >
                  <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size={4}
                  >
                    {(pricingTemplatesResult.warnings.length > 0) && (
                      <Alert
                        type="warning"
                        showIcon
                        message={t(
                          "tools.calculator.lmdPricingWarningTitle",
                          "Last mile delivery is missing from this breakdown",
                        )}
                        description={
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {pricingTemplatesResult.warnings.map((w, i) => (
                              <li key={i} style={{ fontSize: 12 }}>
                                {t(w.key, {
                                  ...w.params,
                                  defaultValue:
                                    w.key === "tools.calculator.lmdRateNotFound"
                                      ? "Last-mile delivery ({{country}}) is not included: No LMD rate found for {{country}} with {{pallets}} pallets or {{cartons}} cartons"
                                      : "Last-mile delivery ({{country}}) could not be calculated",
                                })}
                              </li>
                            ))}
                          </ul>
                        }
                      />
                    )}
                    {[
                      ...pricingTemplatesResult.components,
                      ...(containervervoerResult != null
                        ? [
                            {
                              name: "Containervervoer plaats van bestemming",
                              totalPrice: containervervoerResult.sellingPrice,
                              calculationDetails: containervervoerResult.unknownKm
                                ? t("tools.calculator.unknownKm", "Onbekende kilometers")
                                : (containervervoerResult.calculationFormula ?? `${(containervervoerResult as { distanceKm?: number }).distanceKm?.toFixed(0) ?? 0} km`),
                              componentType: "containervervoer_plaats_van_bestemming",
                            },
                          ]
                        : []),
                    ]
                      .sort(sortCostOverview)
                      .map(
                      (component, index) => {
                        const isTransportComponent =
                          component.name.toLowerCase().includes("transport") ||
                          component.name.toLowerCase().includes("freight");
                        const showVolumetricWeight =
                          isTransportComponent &&
                          transportTypeId === 1 &&
                          weight > 0 &&
                          volume > 0;

                        return (
                          <div key={index} style={{ marginBottom: 6 }}>
                            <Row
                              justify="space-between"
                              style={{ marginBottom: 2 }}
                            >
                              <Text strong>{component.name}</Text>
                              <Text strong>
                                {formatEuro(component.totalPrice)}
                              </Text>
                            </Row>
                            {component.calculationDetails && (
                              <Row>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  {showVolumetricWeight &&
                                  component.calculationDetails.includes(
                                    "kg ×",
                                  ) ? (
                                    // For airfreight transport, calculationDetails already contains all the info
                                    component.calculationDetails
                                  ) : component.calculationDetails ===
                                      `1 × ${formatEuro(component.totalPrice)}` ? (
                                    // Fixed fee: "1 × € X" — skip redundant "= € X"
                                    component.calculationDetails
                                  ) : (
                                    <>
                                      {component.calculationDetails} ={" "}
                                      {formatEuro(component.totalPrice)}
                                    </>
                                  )}
                                </Text>
                              </Row>
                            )}
                          </div>
                        );
                      })}
                    <Divider style={{ margin: "6px 0" }} />
                    <Row justify="space-between">
                      <Text strong style={{ fontSize: 14 }}>
                        {t("tools.calculator.total")}
                      </Text>
                      <Text strong style={{ fontSize: 14 }}>
                        {formatEuro(displayTotal)}
                      </Text>
                    </Row>
                  </Space>
                </Card>
              ) : (
                <Card size="small" bodyStyle={{ padding: "12px" }}>
                  <Text
                    type="secondary"
                    style={{
                      textAlign: "center",
                      display: "block",
                      padding: "4px 0",
                      fontSize: 12,
                    }}
                  >
                    {t(
                      "tools.calculator.fillFieldsToSeeBreakdown",
                      "Fill in the fields to see cost breakdown",
                    )}
                  </Text>
                </Card>
              )}
            </Col>
          </Row>

          {/* Info Alert */}
          <Alert
            message={t("tools.calculator.infoTitle")}
            description={t("tools.calculator.infoDescription")}
            type="info"
            showIcon
            style={{ marginTop: 8, padding: "6px 12px" }}
          />
            </>
          )}
        </CalculatorContainer>
      </Space>
    </div>
  );
};

export default Calculator;
